import { Link } from "react-router-dom";
import type { EventDetailPayload } from "../api";
import { wordsOf } from "../api";
import { ClockDerivation } from "./ClockDerivation";
import { Drawer } from "./Drawer";
import { EventTimeline } from "./EventTimeline";
import { Money, label } from "./Money";
import { Loading, Refused } from "./Shell";
import { toneFor } from "./tone";
import { useApi } from "../useApi";

/**
 * An event, read without leaving the list it was on.
 *
 * Everything the full page holds, ordered by the question a reader
 * actually arrived with: what state is this in and how did it get there,
 * then what happened when, then the money.
 */
export function EventDrawer({ reference, onClose }: {
  reference: string | null;
  onClose: () => void;
}) {
  return (
    <Drawer open={reference !== null} onClose={onClose} title={reference ?? ""}>
      {reference && <Body reference={reference} />}
    </Drawer>
  );
}

function Body({ reference }: { reference: string }) {
  const { data, error, loading } = useApi<EventDetailPayload>(
    `/events/${encodeURIComponent(reference)}`);

  if (loading) return <Loading />;
  if (error) return <Refused status={error.status} detail={error.detail} />;
  if (!data) return null;

  const { event, clocks, correspondence, actions, provenance } = data;
  // The clock worth leading with is the one still worth acting on.
  const lead = clocks.find((c) => toneFor(c) !== "spent") ?? clocks[0];

  return (
    <>
      <div className="drawer__lede">
        <span className="pill">{label(event.event_type)}</span>
        <h3>{event.title}</h3>
        {event.description && <p className="hint">{event.description}</p>}
      </div>

      <div className="drawer__figs">
        <div>
          <span className="k">Prices</span>
          <span className="mn fig"><Money value={event.value_gbp} /></span>
        </div>
        <div>
          <span className="k">Completion</span>
          <span className="mn fig">
            {event.time_impact_days ? `+${Math.round(event.time_impact_days)} days` : "—"}
          </span>
        </div>
      </div>

      {lead && <ClockDerivation clock={lead} />}

      {clocks.length > 1 && (
        <details className="drawer__more">
          <summary>The other {clocks.length - 1} clock
            {clocks.length === 2 ? "" : "s"} on this event</summary>
          {clocks.filter((c) => c !== lead).map((c) => (
            <ClockDerivation key={c.id} clock={c} />
          ))}
        </details>
      )}

      <section>
        <h3 className="deriv__title">What happened, in order</h3>
        <EventTimeline event={event} clocks={clocks} correspondence={correspondence}
          provenance={provenance} />
      </section>

      {actions.length > 0 && (
        <section>
          <h3 className="deriv__title">Waiting on somebody</h3>
          {actions.map((a) => (
            <div key={a.id} className="drawer__action">
              <span>{wordsOf(a.draft_subject) || a.title}</span>
              <span className="k">owner {a.owner}</span>
            </div>
          ))}
        </section>
      )}

      <Link className="btn wide" to={`/events/${encodeURIComponent(reference)}`}>
        Open the full record
      </Link>
    </>
  );
}
